import {
    BILLING_ENTITLEMENT_STATE_SOURCE,
    BillingEntitlementState,
    BillingEntitlementStateService,
    BillingEntitlementStateSource,
} from '@services/billing-entitlement-state.service'
import { BehaviorSubject, combineLatest, distinctUntilChanged, switchMap } from 'rxjs'
import { BillingEntitlementPresentationDirective } from '@app/features/shared/directives/billing-entitlement-presentation.directive'
import { DestroyRef, Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2, forwardRef, inject } from '@angular/core'
import { EntitlementName } from '@app/domain/Entitlement'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Directive({
    selector: '[appBillingEntitlement]',
    providers: [
        {
            provide: BILLING_ENTITLEMENT_STATE_SOURCE,
            useExisting: forwardRef(() => BillingEntitlementDirective),
        },
    ],
    hostDirectives: [
        {
            directive: BillingEntitlementPresentationDirective,
            inputs: [
                'appBillingEntitlementLockedTitle',
                'appBillingEntitlementLockedSubtitle',
                'appBillingEntitlementLockedMessage',
            ],
        },
    ],
})
export class BillingEntitlementDirective implements BillingEntitlementStateSource, OnInit, OnDestroy {

    private entitlementStateService = inject(BillingEntitlementStateService)
    private destroyRef = inject(DestroyRef)
    private presentation = inject(BillingEntitlementPresentationDirective)
    private elementRef = inject(ElementRef<HTMLElement>)
    private renderer = inject(Renderer2)
    private entitlementName$ = new BehaviorSubject<EntitlementName | null>(null)
    private entitlementUsage$ = new BehaviorSubject<number | null>(null)
    private stateSubject = new BehaviorSubject<BillingEntitlementState>('unlocked')
    private state: BillingEntitlementState = 'unlocked'
    private usesQuantityEntitlement = false
    private presentationOnly = false

    readonly billingEntitlementState$ = this.stateSubject.asObservable()

    private clickListener = (event: Event) => this.preventLockedInteraction(event)
    private keydownListener = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            this.preventLockedInteraction(event)
        }
    }

    @Input() set appBillingEntitlement(entitlementName: EntitlementName | null | undefined) {
        const name = entitlementName ?? null
        this.setState(name === null ? 'unlocked' : 'loading')
        this.entitlementName$.next(name)
    }

    @Input() set appBillingEntitlementUsage(currentUsage: number | null | undefined) {
        this.usesQuantityEntitlement = true
        const normalizedUsage = typeof currentUsage === 'number'
            && Number.isInteger(currentUsage)
            && currentUsage >= 0
            ? currentUsage
            : null
        this.entitlementUsage$.next(normalizedUsage)
    }

    @Input() set appBillingEntitlementPresentationOnly(presentationOnly: boolean | null | undefined) {
        this.presentationOnly = presentationOnly === true
        this.updateLockedInteractionState()
    }

    ngOnInit() {
        const host = this.elementRef.nativeElement
        host.addEventListener('click', this.clickListener, true)
        host.addEventListener('keydown', this.keydownListener, true)

        combineLatest([
            this.entitlementName$,
            this.entitlementUsage$,
        ])
            .pipe(
                distinctUntilChanged((previous, current) => {
                    return previous[0] === current[0] && previous[1] === current[1]
                }),
                switchMap(([entitlementName, currentUsage]) => {
                    return this.usesQuantityEntitlement
                        ? this.entitlementStateService.getState$(entitlementName, currentUsage)
                        : this.entitlementStateService.getState$(entitlementName)
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(state => this.setState(state))
    }

    ngOnDestroy() {
        const host = this.elementRef.nativeElement
        host.removeEventListener('click', this.clickListener, true)
        host.removeEventListener('keydown', this.keydownListener, true)
    }

    private setState(state: BillingEntitlementState) {
        if (this.state === state) {
            return
        }
        this.state = state
        this.stateSubject.next(state)
        this.presentation.setState(state)
        this.updateLockedInteractionState()
    }

    private updateLockedInteractionState() {
        const host = this.elementRef.nativeElement
        if (this.state === 'locked' && !this.presentationOnly) {
            this.renderer.setAttribute(host, 'aria-disabled', 'true')
        } else {
            this.renderer.removeAttribute(host, 'aria-disabled')
        }
    }

    private preventLockedInteraction(event: Event) {
        if (this.presentationOnly && this.state !== 'loading') {
            return
        }
        if (this.state === 'unlocked') {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
    }
}
